import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "accent" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg" | "xl";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: string;
  children?: ReactNode;
}

const VARIANTS: Record<Variant, string> = {
  primary: "bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-200",
  accent: "bg-accent-500 text-foreground-900 hover:bg-accent-600 focus:ring-accent-200",
  secondary: "bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-200",
  outline: "bg-transparent border border-background-300 text-foreground-700 hover:bg-background-100 focus:ring-background-200",
  ghost: "bg-transparent text-foreground-600 hover:bg-background-100 focus:ring-background-200",
  danger: "bg-red-500 text-white hover:bg-red-600 focus:ring-red-200",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 px-3.5 text-[13px]",
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-6 text-[15px]",
  xl: "h-14 px-8 text-base",
};

export default function Button({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  icon,
  children,
  className = "",
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-btn font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer select-none focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${VARIANTS[variant]} ${SIZES[size]} ${fullWidth ? "w-full" : ""} ${className}`}
      {...rest}
    >
      {loading ? (
        <i className="ri-loader-4-line animate-spin text-lg"></i>
      ) : icon ? (
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-lg`}></i>
        </div>
      ) : null}
      {children}
    </button>
  );
}